import React, { useRef, useState } from "react";
import { FaFileArchive, FaFilePdf, FaWhatsapp, FaUpload, FaTimes } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { orderVerificationApi } from "../../api/varificationService";

export default function DesignUploadForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // =========================================================
  // ORDER ID
  // =========================================================
  // Supports:
  // 1. /order-verification/:orderId
  // 2. navigate("/order-verification", { state: { orderId } })
  // 3. /order-verification?orderId=123
  // =========================================================

  const orderId =
    params?.orderId ||
    location?.state?.orderId ||
    new URLSearchParams(location.search).get("orderId") ||
    "";

  // =========================================================
  // STATE
  // =========================================================

  const [designFile, setDesignFile] = useState(null);

  const [finalDesignProof, setFinalDesignProof] = useState([]);

  const [whatsappScreenshot, setWhatsappScreenshot] = useState(null);

  const [dragActive, setDragActive] = useState(null);

  const [error, setError] = useState("");

  const [backendErrors, setBackendErrors] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  // =========================================================
  // REFS
  // =========================================================

  const designInputRef = useRef(null);

  const proofInputRef = useRef(null);

  const whatsappInputRef = useRef(null);

  // =========================================================
  // CLEAR ERRORS
  // =========================================================

  const clearErrors = () => {
    setError("");
    setBackendErrors({});
  };

  // =========================================================
  // FILE SIZE
  // =========================================================

  const formatFileSize = (bytes) => {
    if (!bytes) {
      return "0 KB";
    }

    const kb = bytes / 1024;

    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    const mb = kb / 1024;

    return `${mb.toFixed(2)} MB`;
  };

  // =========================================================
  // FILE TO BASE64
  // =========================================================
  // IMPORTANT:
  // We keep the complete Data URL.
  //
  // Example:
  // data:application/zip;base64,UEsDB...
  //
  // data:application/pdf;base64,JVBER...
  //
  // data:image/png;base64,iVBOR...
  // =========================================================

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error("File is required"));
        return;
      }

      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => {
        if (typeof reader.result !== "string") {
          reject(new Error("Unable to convert file to Base64"));
          return;
        }

        resolve(reader.result);
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };
    });
  };

  // =========================================================
  // DESIGN FILE VALIDATION
  // =========================================================

  const handleDesignFile = (file) => {
    clearErrors();

    if (!file) {
      return;
    }

    const fileName = file.name.toLowerCase();

    const isZip =
      fileName.endsWith(".zip") ||
      file.type === "application/zip" ||
      file.type === "application/x-zip-compressed";

    if (!isZip) {
      setError("Only ZIP files are allowed for Design File.");

      return;
    }

    setDesignFile(file);
  };

  // =========================================================
  // FINAL DESIGN PROOF VALIDATION
  // =========================================================

  const handleProofFiles = (fileList) => {
    clearErrors();

    if (!fileList || fileList.length === 0) {
      return;
    }

    const allowedExtensions = [".pdf", ".jpg", ".jpeg", ".png", ".webp"];

    const selectedFiles = Array.from(fileList);

    const validFiles = [];
    const invalidFiles = [];

    selectedFiles.forEach((file) => {
      const fileName = file.name.toLowerCase();

      const isValid = allowedExtensions.some((extension) => fileName.endsWith(extension));

      if (isValid) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      setError("Only PDF, JPG, JPEG, PNG or WEBP files are allowed for Final Design Proof.");
    }

    if (validFiles.length === 0) {
      return;
    }

    setFinalDesignProof((previousFiles) => {
      const existingKeys = new Set(
        previousFiles.map((file) => `${file.name}-${file.size}-${file.lastModified}`),
      );

      const newFiles = validFiles.filter((file) => {
        const key = `${file.name}-${file.size}-${file.lastModified}`;

        return !existingKeys.has(key);
      });

      const combinedFiles = [...previousFiles, ...newFiles];

      if (combinedFiles.length > 5) {
        setError("Maximum 5 Final Design Proof files are allowed.");
      }

      return combinedFiles.slice(0, 5);
    });
  };

  // =========================================================
  // WHATSAPP SCREENSHOT VALIDATION
  // =========================================================

  const handleWhatsappFile = (file) => {
    clearErrors();

    if (!file) {
      return;
    }

    const allowedExtensions = [".jpg", ".jpeg", ".png", ".webp"];

    const fileName = file.name.toLowerCase();

    const isValid = allowedExtensions.some((extension) => fileName.endsWith(extension));

    if (!isValid) {
      setError("WhatsApp Screenshot must be JPG, JPEG, PNG or WEBP.");

      return;
    }

    setWhatsappScreenshot(file);
  };

  // =========================================================
  // DRAG ENTER
  // =========================================================

  const handleDragEnter = (event, type) => {
    event.preventDefault();
    event.stopPropagation();

    if (isSubmitting) {
      return;
    }

    setDragActive(type);
  };

  // =========================================================
  // DRAG OVER
  // =========================================================

  const handleDragOver = (event, type) => {
    event.preventDefault();
    event.stopPropagation();

    if (isSubmitting) {
      return;
    }

    setDragActive(type);
  };

  // =========================================================
  // DRAG LEAVE
  // =========================================================

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setDragActive(null);
  };

  // =========================================================
  // DROP
  // =========================================================

  const handleDrop = (event, type) => {
    event.preventDefault();
    event.stopPropagation();

    setDragActive(null);

    if (isSubmitting) {
      return;
    }

    const files = event.dataTransfer.files;

    if (!files || files.length === 0) {
      return;
    }

    if (type === "design") {
      handleDesignFile(files[0]);

      return;
    }

    if (type === "proof") {
      handleProofFiles(files);

      return;
    }

    if (type === "whatsapp") {
      handleWhatsappFile(files[0]);
    }
  };

  // =========================================================
  // REMOVE DESIGN FILE
  // =========================================================

  const removeDesignFile = () => {
    setDesignFile(null);

    if (designInputRef.current) {
      designInputRef.current.value = "";
    }
  };

  // =========================================================
  // REMOVE PROOF FILE
  // =========================================================

  const removeProofFile = (index) => {
    setFinalDesignProof((previousFiles) =>
      previousFiles.filter((_, fileIndex) => fileIndex !== index),
    );

    if (proofInputRef.current) {
      proofInputRef.current.value = "";
    }
  };

  // =========================================================
  // REMOVE WHATSAPP FILE
  // =========================================================

  const removeWhatsappFile = () => {
    setWhatsappScreenshot(null);

    if (whatsappInputRef.current) {
      whatsappInputRef.current.value = "";
    }
  };

  // =========================================================
  // BACKEND ERROR PARSER
  // =========================================================

  const parseBackendError = (error) => {
    const responseData = error?.response?.data;

    console.log("Backend error response:", responseData);

    if (!responseData) {
      return {
        message: "Something went wrong. Please try again.",
        errors: {},
      };
    }

    const backendError = responseData?.error;

    if (backendError && typeof backendError === "object" && !Array.isArray(backendError)) {
      const formattedErrors = {};

      Object.entries(backendError).forEach(([field, message]) => {
        formattedErrors[field] = Array.isArray(message) ? message.join(", ") : String(message);
      });

      return {
        message: responseData?.message || "Validation failed.",
        errors: formattedErrors,
      };
    }

    return {
      message: responseData?.message || "Something went wrong.",
      errors: {},
    };
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async () => {
    clearErrors();

    // =======================================================
    // ORDER ID VALIDATION
    // =======================================================

    if (!orderId) {
      setError("Order ID is missing. Please open verification from the order.");

      return;
    }

    // =======================================================
    // FILE VALIDATION
    // =======================================================

    if (!designFile) {
      setError("Please upload the Design ZIP file.");

      return;
    }

    if (finalDesignProof.length === 0) {
      setError("Please upload at least one Final Design Proof.");

      return;
    }

    if (!whatsappScreenshot) {
      setError("Please upload the WhatsApp Screenshot.");

      return;
    }

    try {
      setIsSubmitting(true);

      // =====================================================
      // DESIGN ZIP → BASE64
      // =====================================================

      const designFileBase64 = await fileToBase64(designFile);

      // =====================================================
      // FINAL DESIGN PROOF → BASE64
      // =====================================================

      const finalDesignProofBase64 = await Promise.all(
        finalDesignProof.map((file) => fileToBase64(file)),
      );

      // =====================================================
      // WHATSAPP SCREENSHOT → BASE64
      // =====================================================

      const whatsappScreenshotBase64 = await fileToBase64(whatsappScreenshot);

      // =====================================================
      // FINAL PAYLOAD
      // =====================================================

      const payload = {
        orderId: orderId,

        design_file: designFileBase64,

        files: finalDesignProofBase64,

        screenShot: whatsappScreenshotBase64,
      };

      // =====================================================
      // API CALL
      // =====================================================

      const response = await orderVerificationApi(payload);

      // =====================================================
      // SUCCESS
      // =====================================================

      if (response?.success === true) {
        setError("");
        setBackendErrors({});

        navigate("/orders-list");

        return;
      }

      // =====================================================
      // SUCCESS FALSE
      // =====================================================

      setError(response?.message || "Order verification failed.");
    } catch (error) {
      console.error("Order verification error:", error);

      const parsedError = parseBackendError(error);

      setError(parsedError.message);

      setBackendErrors(parsedError.errors);
    } finally {
      setIsSubmitting(false);
    }
  };

  // =========================================================
  // UPLOAD BOX STYLE
  // =========================================================

  const getUploadBoxClass = (type) => {
    if (dragActive === type) {
      return "bg-primary-subtle border-primary";
    }

    return "bg-white border-secondary";
  };

  // =========================================================
  // JSX
  // =========================================================

  return (
    <div className="container-fluid py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-9 col-xl-8">
          <div className="card border-0 shadow-sm">
            {/* =================================================
                HEADER
            ================================================= */}

            <div className="card-header bg-white border-bottom p-4">
              
            </div>

            {/* =================================================
                BODY
            ================================================= */}

            <div className="card-body p-4">
              {/* =================================================
                  ERROR ALERT
              ================================================= */}

              {(error || Object.keys(backendErrors).length > 0) && (
                <div className="alert alert-danger position-relative" role="alert">
                  <div className="fw-semibold mb-2">
                    {error || "Please correct the following errors."}
                  </div>

                  {Object.keys(backendErrors).length > 0 && (
                    <ul className="mb-0 ps-3">
                      {Object.entries(backendErrors).map(([field, message]) => (
                        <li key={field}>
                          <strong>{field}:</strong> {message}
                        </li>
                      ))}
                    </ul>
                  )}

                  <button
                    type="button"
                    className="btn-close position-absolute top-0 end-0 m-3"
                    onClick={clearErrors}
                    aria-label="Close"
                  />
                </div>
              )}

              <div className="row g-4">
                {/* =================================================
                    DESIGN ZIP
                ================================================= */}

                <div className="col-12">
                  <div className="border rounded-3 p-4 bg-light">
                    <div className="d-flex align-items-start gap-3 mb-3">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3 bg-dark text-white flex-shrink-0"
                        style={{
                          width: "48px",
                          height: "48px",
                        }}
                      >
                        <FaFileArchive size={22} />
                      </div>

                      <div>
                        <h6 className="fw-bold mb-1">
                          Design File
                          <span className="text-danger ms-1">*</span>
                        </h6>

                        <p className="text-muted small mb-0">
                          Upload the original design ZIP file.
                        </p>
                      </div>
                    </div>

                    {/* UPLOAD BOX */}

                    <div
                      className={`w-100 text-center rounded-3 p-4 border border-2 border-dashed ${getUploadBoxClass(
                        "design",
                      )}`}
                      style={{
                        cursor: isSubmitting ? "not-allowed" : "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onDragEnter={(e) => handleDragEnter(e, "design")}
                      onDragOver={(e) => handleDragOver(e, "design")}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, "design")}
                      onClick={() => {
                        if (!isSubmitting && designInputRef.current) {
                          designInputRef.current.click();
                        }
                      }}
                    >
                      <FaUpload size={30} className="text-secondary mb-3" />

                      <div className="fw-semibold">
                        {dragActive === "design"
                          ? "Drop ZIP file here"
                          : "Drag & Drop ZIP file here"}
                      </div>

                      <small className="text-muted">or click to browse</small>

                      <div className="small text-muted mt-2">Only .zip files are allowed</div>

                      <input
                        ref={designInputRef}
                        type="file"
                        accept=".zip,application/zip"
                        className="d-none"
                        disabled={isSubmitting}
                        onChange={(e) => {
                          const file = e.target.files?.[0];

                          if (file) {
                            handleDesignFile(file);
                          }

                          e.target.value = "";
                        }}
                      />
                    </div>

                    {/* SELECTED DESIGN FILE */}

                    {designFile && (
                      <div className="mt-3">
                        <div className="d-flex align-items-center justify-content-between border rounded-3 bg-white p-3">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="d-flex align-items-center justify-content-center bg-dark text-white rounded-2"
                              style={{
                                width: "40px",
                                height: "40px",
                              }}
                            >
                              <FaFileArchive />
                            </div>

                            <div>
                              <div className="fw-semibold text-break">{designFile.name}</div>

                              <small className="text-muted">
                                {formatFileSize(designFile.size)}
                              </small>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            disabled={isSubmitting}
                            onClick={removeDesignFile}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* =================================================
                    FINAL DESIGN PROOF
                ================================================= */}

                <div className="col-12">
                  <div className="border rounded-3 p-4 bg-light">
                    <div className="d-flex align-items-start gap-3 mb-3">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3 bg-primary text-white flex-shrink-0"
                        style={{
                          width: "48px",
                          height: "48px",
                        }}
                      >
                        <FaFilePdf size={22} />
                      </div>

                      <div>
                        <h6 className="fw-bold mb-1">
                          Final Design Proof
                          <span className="text-danger ms-1">*</span>
                        </h6>

                        <p className="text-muted small mb-0">
                          Upload the final approved design proof.
                        </p>
                      </div>
                    </div>

                    {/* UPLOAD BOX */}

                    <div
                      className={`w-100 text-center rounded-3 p-4 border border-2 border-dashed ${getUploadBoxClass(
                        "proof",
                      )}`}
                      style={{
                        cursor: isSubmitting ? "not-allowed" : "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onDragEnter={(e) => handleDragEnter(e, "proof")}
                      onDragOver={(e) => handleDragOver(e, "proof")}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, "proof")}
                      onClick={() => {
                        if (!isSubmitting && proofInputRef.current) {
                          proofInputRef.current.click();
                        }
                      }}
                    >
                      <FaUpload size={30} className="text-secondary mb-3" />

                      <div className="fw-semibold">
                        {dragActive === "proof"
                          ? "Drop files here"
                          : "Drag & Drop design proofs here"}
                      </div>

                      <small className="text-muted">or click to browse</small>

                      <div className="small text-muted mt-2">
                        PDF, JPG, JPEG, PNG or WEBP • Maximum 5 files
                      </div>

                      <input
                        ref={proofInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        className="d-none"
                        disabled={isSubmitting}
                        onChange={(e) => {
                          const files = e.target.files;

                          if (files?.length) {
                            handleProofFiles(files);
                          }

                          e.target.value = "";
                        }}
                      />
                    </div>

                    {/* FILE COUNT */}

                    {finalDesignProof.length > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
                        <span className="fw-semibold">Selected Files</span>

                        <span className="badge bg-primary">
                          {finalDesignProof.length}
                          /5
                        </span>
                      </div>
                    )}

                    {/* FILE LIST */}

                    {finalDesignProof.length > 0 && (
                      <div>
                        {finalDesignProof.map((file, index) => (
                          <div
                            key={`${file.name}-${file.size}-${file.lastModified}`}
                            className="d-flex align-items-center justify-content-between border rounded-3 bg-white p-3 mb-2"
                          >
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="d-flex align-items-center justify-content-center bg-primary text-white rounded-2"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                }}
                              >
                                <FaFilePdf />
                              </div>

                              <div>
                                <div className="fw-semibold text-break">{file.name}</div>

                                <small className="text-muted">{formatFileSize(file.size)}</small>
                              </div>
                            </div>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              disabled={isSubmitting}
                              onClick={() => removeProofFile(index)}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* =================================================
                    WHATSAPP SCREENSHOT
                ================================================= */}

                <div className="col-12">
                  <div className="border rounded-3 p-4 bg-light">
                    <div className="d-flex align-items-start gap-3 mb-3">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3 bg-success text-white flex-shrink-0"
                        style={{
                          width: "48px",
                          height: "48px",
                        }}
                      >
                        <FaWhatsapp size={24} />
                      </div>

                      <div>
                        <h6 className="fw-bold mb-1">
                          WhatsApp Screenshot
                          <span className="text-danger ms-1">*</span>
                        </h6>

                        <p className="text-muted small mb-0">
                          Upload the WhatsApp conversation screenshot.
                        </p>
                      </div>
                    </div>

                    {/* UPLOAD BOX */}

                    <div
                      className={`w-100 text-center rounded-3 p-4 border border-2 border-dashed ${getUploadBoxClass(
                        "whatsapp",
                      )}`}
                      style={{
                        cursor: isSubmitting ? "not-allowed" : "pointer",
                        transition: "all 0.2s ease",
                      }}
                      onDragEnter={(e) => handleDragEnter(e, "whatsapp")}
                      onDragOver={(e) => handleDragOver(e, "whatsapp")}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, "whatsapp")}
                      onClick={() => {
                        if (!isSubmitting && whatsappInputRef.current) {
                          whatsappInputRef.current.click();
                        }
                      }}
                    >
                      <FaWhatsapp size={32} className="text-success mb-3" />

                      <div className="fw-semibold">
                        {dragActive === "whatsapp"
                          ? "Drop screenshot here"
                          : "Drag & Drop screenshot here"}
                      </div>

                      <small className="text-muted">or click to browse</small>

                      <div className="small text-muted mt-2">
                        JPG, JPEG, PNG or WEBP • Only 1 image
                      </div>

                      <input
                        ref={whatsappInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp"
                        className="d-none"
                        disabled={isSubmitting}
                        onChange={(e) => {
                          const file = e.target.files?.[0];

                          if (file) {
                            handleWhatsappFile(file);
                          }

                          e.target.value = "";
                        }}
                      />
                    </div>

                    {/* SELECTED WHATSAPP FILE */}

                    {whatsappScreenshot && (
                      <div className="mt-3">
                        <div className="d-flex align-items-center justify-content-between border rounded-3 bg-white p-3">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="d-flex align-items-center justify-content-center bg-success text-white rounded-2"
                              style={{
                                width: "40px",
                                height: "40px",
                              }}
                            >
                              <FaWhatsapp />
                            </div>

                            <div>
                              <div className="fw-semibold text-break">
                                {whatsappScreenshot.name}
                              </div>

                              <small className="text-muted">
                                {formatFileSize(whatsappScreenshot.size)}
                              </small>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            disabled={isSubmitting}
                            onClick={removeWhatsappFile}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* =================================================
                    SUBMIT
                ================================================= */}

                <div className="col-12">
                  <div className="border-top pt-4 mt-2 d-flex justify-content-end gap-2">
                    {/* SUBMIT */}

                    <button
                      type="button"
                      className="btn btn-primary px-4 d-inline-flex align-items-center justify-content-center"
                      style={{
                        minHeight: "38px",
                        minWidth: "190px",
                      }}
                      disabled={isSubmitting}
                      onClick={handleSubmit}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                            style={{
                              width: "1rem",
                              height: "1rem",
                            }}
                          />

                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <FaUpload className="me-2" />

                          <span>Submit for Verification</span>
                        </>
                      )}
                    </button>

                    {/* CANCEL */}

                    <button
                      type="button"
                      className="btn"
                      style={{
                        backgroundColor: "#032B4D",
                        color: "#fff",
                        minHeight: "38px",
                      }}
                      disabled={isSubmitting}
                      onClick={() => navigate("/orders-list")}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
